/*
 * epl-range-lock.js
 *
 * PROBLEMA QUE RESUELVE
 * El precio por volumen esta guardado como OPCION DE VARIANTE ("Cantidad" o
 * "Rango de Cantidad", con valores tipo "6 - 11 und."). El stepper nativo de
 * Dawn envia a /cart/change.js solo { line, quantity } y nunca toca la variante,
 * asi que al cruzar un tramo el cliente seguia pagando el precio del tramo
 * anterior. Como el cobro sale del precio de la variante, el error llegaba al
 * checkout.
 *
 * QUE HACE
 * Sustituye el stepper nativo por uno que SI resuelve el tramo:
 *   - mismo tramo  -> /cart/change.js { id, quantity, properties }  (1 llamada,
 *     una sola linea; se comprobo que change.js acepta properties)
 *   - otro tramo   -> /cart/add.js con la variante correcta y despues se elimina
 *     la linea anterior. Se anade ANTES de eliminar: si el add falla, el cliente
 *     no pierde nada.
 * Ademas limpia la ficha de la linea: esconde el tramo de variante y la propiedad
 * "Cantidad Seleccionada", que repetia la cantidad ya visible en el stepper.
 *
 * PRECIO INSTANTANEO (paintOptimistic)
 * En la ficha el cambio se siente inmediato porque range_price.liquid lleva el
 * mapa de precios incrustado y recalcula SIN RED. En el carrito habia que esperar
 * al servidor (~425 ms la mutacion + ~236 ms la seccion) y se percibia como
 * lentitud. Ahora el precio unitario y el total de la linea se pintan en el mismo
 * clic con el precio de la variante destino, que ya esta en infoCache.
 *
 * El descuento se replica exactamente: se lee value_type/value/allocation_method
 * de line_level_discount_allocations. Para un porcentaje asignado "each" (el caso
 * de BIENVENIDOS8) Shopify TRUNCA el descuento por unidad y luego multiplica por
 * la cantidad; verificado: floor(398*0.08)=31 -> 367 c/u -> 367*24 = 8808.
 * Aplicar la razon al total de linea daba 12 centavos de diferencia, por eso se
 * calcula por unidad. Cualquier otro tipo de descuento cae en una aproximacion
 * por proporcion y la reconciliacion posterior deja el importe autoritativo.
 *
 * Marcado que se parchea (verificado en esta tienda, con y sin descuento):
 *   con descuento -> <div class="cart-item__discounted-prices">
 *                      <s class="cart-item__old-price ...">        ORIGINAL
 *                      <strong|span class="...final-price|price">  FINAL
 *   sin descuento -> un unico nodo de precio
 *
 * SIN PARPADEO
 * El refresco se hace aqui, NO publicando cartUpdate a secas. Publicarlo dispara
 * el onCartUpdate() de Dawn, que reemplaza la linea y el pie con HTML recien
 * renderizado (sin las mejoras) y obligaba a reaplicarlas despues via observer +
 * antirrebote + peticion a /cart.js. Ahora se pide la seccion, se aplican las
 * mejoras SOBRE EL DOCUMENTO YA PARSEADO (fuera del DOM) y solo entonces se
 * inserta, asi que el estado sin procesar nunca llega a pintarse.
 *   - infoCache guarda la info de tramos ya resuelta, para mejorar de forma
 *     sincrona sin esperar red.
 *   - el intercambio usa replaceWith (adopcion de nodos), NO innerHTML: con
 *     innerHTML se serializa el HTML y se perderian los listeners del stepper.
 * Se publica cartUpdate con source 'cart-items' porque es la guarda que Dawn usa
 * para no auto-refrescarse.
 *
 * SIN SALTO DE SCROLL
 * <cart-drawer-items> es el contenedor con overflow-y:auto, es decir EL que hace
 * scroll. Al reemplazarlo con replaceWith se destruia su scrollTop y el cliente
 * volvia arriba del carrito tras cada cambio. Se guarda la posicion antes del
 * intercambio y se restaura sobre el nodo nuevo en el mismo bloque sincrono.
 *
 * DETECCION
 * No se compara por titulo. El intento previo (ocultarCantidadCarrito, en
 * range_price.liquid) dependia de .product__title, que no existe en /cart, asi
 * que hacia return y nunca se ejecutaba. Aqui se comprueba:
 *   1. que la linea tenga una opcion con valor de rango
 *   2. que el producto tenga MAS DE UN tramo en esa opcion
 * El punto 2 evita tocar productos de una sola variante como "Amplificador\n * Souno Vibes" ("1 - 2 und.", precio plano).
 */
(function () {
  'use strict';

  var RANGE_RE = /^\s*(\d+)\s*(?:[-–—]\s*(\d+)\s*)?und\.?\s*$/i;
  var LOCK_CLASS = 'epl-range-locked';
  var COMMIT_DELAY = 180;
  var LINE_SELECTOR = '[id^="CartDrawer-Item-"], [id^="CartItem-"]';

  var pendingProduct = new Map();
  var infoCache = new Map();
  var running = false;
  var queued = false;
  var scanTimer = null;
  var suppressScan = false;

  function clean(text) {
    return String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
  }

  function parseRange(value) {
    var m = clean(value).match(RANGE_RE);
    if (!m) return null;
    return { value: clean(value), min: parseInt(m[1], 10), max: m[2] ? parseInt(m[2], 10) : null };
  }

  function isRangeValue(value) {
    return parseRange(value) !== null;
  }

  function money(cents) {
    var parts = (Math.round(cents) / 100).toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function getProductInfo(handle) {
    if (infoCache.has(handle)) return Promise.resolve(infoCache.get(handle));
    if (pendingProduct.has(handle)) return pendingProduct.get(handle);

    var promise = fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('producto no disponible');
        return r.json();
      })
      .then(function (product) {
        var variants = product.variants || [];
        var optionCount = (variants[0] && variants[0].options && variants[0].options.length) || 0;
        var rangeIdx = -1;
        var best = 0;

        for (var i = 0; i < optionCount; i++) {
          var distinct = new Set();
          for (var j = 0; j < variants.length; j++) {
            var v = variants[j].options && variants[j].options[i];
            if (isRangeValue(v)) distinct.add(clean(v).toLowerCase());
          }
          if (distinct.size > best) { best = distinct.size; rangeIdx = i; }
        }

        var info;
        if (best < 2) {
          info = { tiered: false };
        } else {
          var seen = Object.create(null);
          var tiers = [];
          variants.forEach(function (v) {
            var parsed = parseRange(v.options[rangeIdx]);
            if (!parsed) return;
            var k = parsed.value.toLowerCase();
            if (seen[k]) return;
            seen[k] = true;
            tiers.push(parsed);
          });
          tiers.sort(function (a, b) { return a.min - b.min; });

          /* Un numero suelto en el ultimo tramo significa "y mas" (Taza: "100 und.").
             En cualquier otra posicion significa esa cantidad exacta ("1 und."). */
          tiers.forEach(function (t, i) {
            if (t.max === null) t.max = i === tiers.length - 1 ? Infinity : t.min;
          });

          info = {
            tiered: true, variants: variants, rangeIdx: rangeIdx, tiers: tiers,
            min: tiers[0].min, max: tiers[tiers.length - 1].max
          };
        }

        infoCache.set(handle, info);
        pendingProduct.delete(handle);
        return info;
      })
      .catch(function () {
        var info = { tiered: false };
        infoCache.set(handle, info);
        pendingProduct.delete(handle);
        return info;
      });

    pendingProduct.set(handle, promise);
    return promise;
  }

  function resolveTier(info, qty) {
    var tiers = info.tiers;
    for (var i = 0; i < tiers.length; i++) {
      if (qty >= tiers[i].min && qty <= tiers[i].max) return tiers[i];
    }
    var fallback = tiers[0];
    for (var j = 0; j < tiers.length; j++) {
      if (tiers[j].min <= qty) fallback = tiers[j];
    }
    return fallback;
  }

  function resolveVariant(info, currentVariantId, tier) {
    var current = info.variants.filter(function (v) { return v.id === currentVariantId; })[0];
    if (!current) return null;
    var target = tier.value.toLowerCase();
    return info.variants.filter(function (v) {
      return v.options.every(function (val, i) {
        if (i === info.rangeIdx) return clean(val).toLowerCase() === target;
        return val === current.options[i];
      });
    })[0] || null;
  }

  function buildProperties(item, newQty) {
    var props = {};
    var source = item.properties || {};
    var hadSelected = false;
    Object.keys(source).forEach(function (k) {
      if (k.charAt(0) === '_') return;
      if (k === 'Cantidad Seleccionada') { hadSelected = true; props[k] = String(newQty); return; }
      props[k] = source[k];
    });
    return { properties: props, changed: hadSelected };
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw new Error((data && data.description) || 'error del carrito');
        return data;
      });
    });
  }

  function lineElement(root, index) {
    var getById = root.getElementById ? root.getElementById.bind(root) : function (id) {
      return root.querySelector('#' + id);
    };
    return getById('CartDrawer-Item-' + index) || getById('CartItem-' + index);
  }

  function setLineLoading(stepper, loading) {
    var line = stepper.closest(LINE_SELECTOR);
    if (!line) return;
    Array.prototype.forEach.call(line.querySelectorAll('.loading__spinner'), function (s) {
      s.classList.toggle('hidden', !loading);
    });
  }

  /* --- Pintado instantaneo de precios ------------------------------------- */

  function paintPriceBox(box, original, final) {
    if (!box) return;
    var disc = box.classList && box.classList.contains('cart-item__discounted-prices')
      ? box
      : box.querySelector('.cart-item__discounted-prices');

    if (disc) {
      var oldEl = disc.querySelector('s.cart-item__old-price');
      if (oldEl) oldEl.textContent = money(original);
      var finalEl = disc.querySelector('.cart-item__final-price') ||
                    disc.querySelector('span.price:not(.cart-item__old-price)');
      if (finalEl) finalEl.textContent = money(final);
      return;
    }

    /* Sin descuento: un unico nodo de precio */
    var single = box.querySelector('.price');
    if (single) { single.textContent = money(final); return; }
    if (/\$/.test(box.textContent)) box.textContent = money(final);
  }

  function unitPriceBox(line) {
    var details = line.querySelector('.cart-item__details');
    if (!details) return null;
    var disc = details.querySelector('.cart-item__discounted-prices');
    if (disc) return disc;
    var kids = details.children;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].tagName === 'DL') continue;
      if (kids[i].classList.contains('product-option') && /\$/.test(kids[i].textContent)) return kids[i];
    }
    return null;
  }

  function totalPriceBox(line) {
    return line.querySelector('.cart-item__totals .cart-item__price-wrapper');
  }

  /* Replica el calculo de Shopify sobre el precio unitario. Para un porcentaje
     asignado "each" el descuento por unidad se TRUNCA (verificado:
     floor(398*0.08)=31, floor(442*0.08)=35) y el total es unitario * cantidad. */
  function discountedUnit(item, unitOriginal) {
    var alloc = (item.line_level_discount_allocations || [])[0];
    var app = alloc && alloc.discount_application;

    if (app && app.value_type === 'percentage' && app.allocation_method === 'each') {
      var pct = parseFloat(app.value) / 100;
      if (!isNaN(pct)) return unitOriginal - Math.floor(unitOriginal * pct);
    }

    /* Otros tipos de descuento: aproximacion por proporcion. La reconciliacion
       posterior deja el importe autoritativo. */
    var ratio = item.original_price > 0 ? item.final_price / item.original_price : 1;
    return Math.round(unitOriginal * ratio);
  }

  function paintOptimistic(stepper, item, info, newQty) {
    var line = stepper.closest(LINE_SELECTOR);
    if (!line) return;
    var target = resolveVariant(info, item.variant_id, resolveTier(info, newQty));
    if (!target || typeof target.price !== 'number') return;

    var unitOriginal = target.price;
    var unitFinal = discountedUnit(item, unitOriginal);

    paintPriceBox(unitPriceBox(line), unitOriginal, unitFinal);
    paintPriceBox(totalPriceBox(line), unitOriginal * newQty, unitFinal * newQty);
  }

  /* --- Refresco sin parpadeo ni salto de scroll --------------------------- */

  function fetchSection(id) {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(cartUrl + '?section_id=' + id)
      .then(function (r) { return r.text(); })
      .then(function (text) { return new DOMParser().parseFromString(text, 'text/html'); });
  }

  function enhanceDocument(root, cartData) {
    (cartData.items || []).forEach(function (item, index) {
      var el = lineElement(root, index + 1);
      if (!el) return;
      var info = infoCache.get(item.handle);
      if (info && info.tiered) enhanceLine(el, item, info);
    });
  }

  function swapNode(doc, selector) {
    var target = document.querySelector(selector);
    var source = doc.querySelector(selector);
    if (target && source) target.replaceWith(source);
  }

  function refreshCart(cartData) {
    var active = document.activeElement;
    var focusedLine = null;
    if (active && active.classList && active.classList.contains('epl-qty-edit__input')) {
      var host = active.closest(LINE_SELECTOR);
      focusedLine = host ? host.id : null;
    }

    var scroller = document.querySelector('cart-drawer-items');
    var savedScroll = scroller ? scroller.scrollTop : 0;
    var savedWindowScroll = window.scrollY;

    suppressScan = true;

    var isDrawer = !!document.querySelector('cart-drawer cart-drawer-items');
    var job;

    if (isDrawer) {
      job = fetchSection('cart-drawer').then(function (doc) {
        enhanceDocument(doc, cartData);
        swapNode(doc, 'cart-drawer-items');
        swapNode(doc, '.cart-drawer__footer');
        /* cart-drawer-items ES el contenedor con overflow-y:auto */
        var fresh = document.querySelector('cart-drawer-items');
        if (fresh) fresh.scrollTop = savedScroll;
      });
    } else {
      job = Promise.all([fetchSection('main-cart-items'), fetchSection('main-cart-footer')])
        .then(function (docs) {
          enhanceDocument(docs[0], cartData);
          swapNode(docs[0], '#main-cart-items .js-contents');
          swapNode(docs[1], '#main-cart-footer .js-contents');
          window.scrollTo(0, savedWindowScroll);
        });
    }

    return job
      .then(function () {
        if (focusedLine) {
          var input = document.querySelector('#' + focusedLine + ' .epl-qty-edit__input');
          if (input) {
            input.focus({ preventScroll: true });
            var len = input.value.length;
            try { input.setSelectionRange(len, len); } catch (e) {}
          }
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cartData });
        }
        return fetchSection('cart-icon-bubble').then(function (doc) {
          var bubble = document.getElementById('cart-icon-bubble');
          var source = doc.querySelector('.shopify-section');
          if (bubble && source) bubble.innerHTML = source.innerHTML;
        });
      })
      .catch(function () {})
      .finally(function () {
        setTimeout(function () { suppressScan = false; }, 250);
      });
  }

  /* --- Stepper ------------------------------------------------------------ */

  function setBusy(stepper, busy) {
    stepper.classList.toggle('epl-qty-edit--busy', !!busy);
    Array.prototype.forEach.call(stepper.querySelectorAll('button, input'), function (el) {
      el.disabled = !!busy;
    });
  }

  function showError(stepper, message) {
    var slot = stepper.querySelector('.epl-qty-edit__error');
    if (slot) slot.textContent = message || '';
  }

  function commit(stepper, item, info) {
    var input = stepper.querySelector('.epl-qty-edit__input');
    var newQty = parseInt(input.value, 10);

    if (isNaN(newQty)) newQty = item.quantity;
    newQty = Math.max(info.min, Math.min(newQty, info.max === Infinity ? 1e9 : info.max));
    input.value = newQty;

    if (newQty === item.quantity) { setLineLoading(stepper, false); return; }

    var target = resolveVariant(info, item.variant_id, resolveTier(info, newQty));

    if (!target) {
      showError(stepper, 'Esa cantidad no esta disponible.');
      input.value = item.quantity;
      setLineLoading(stepper, false);
      return;
    }
    if (target.available === false) {
      showError(stepper, 'Tramo agotado. Escribenos y lo resolvemos.');
      input.value = item.quantity;
      setLineLoading(stepper, false);
      return;
    }

    showError(stepper, '');
    setBusy(stepper, true);

    var built = buildProperties(item, newQty);
    var operation;

    if (target.id === item.variant_id) {
      var body = { id: item.key, quantity: newQty };
      if (built.changed) body.properties = built.properties;
      operation = postJSON('/cart/change.js', body);
    } else {
      operation = postJSON('/cart/add.js', {
        items: [{ id: target.id, quantity: newQty, properties: built.properties }]
      }).then(function () {
        return postJSON('/cart/change.js', { id: item.key, quantity: 0 });
      });
    }

    return operation
      .then(function (cart) { return refreshCart(cart); })
      .catch(function (err) {
        showError(stepper, err.message || 'No se pudo actualizar.');
        input.value = item.quantity;
        setBusy(stepper, false);
        setLineLoading(stepper, false);
      });
  }

  function buildStepper(item, info) {
    var stepper = document.createElement('div');
    stepper.className = 'epl-qty-edit';

    var controls = document.createElement('div');
    controls.className = 'epl-qty-edit__controls';

    var minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'epl-qty-edit__btn';
    minus.setAttribute('aria-label', 'Restar unidad');
    minus.textContent = '−';

    var input = document.createElement('input');
    input.type = 'number';
    input.className = 'epl-qty-edit__input';
    input.value = item.quantity;
    input.min = info.min;
    if (info.max !== Infinity) input.max = info.max;
    input.inputMode = 'numeric';
    input.setAttribute('aria-label', 'Cantidad de unidades');

    var plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'epl-qty-edit__btn';
    plus.setAttribute('aria-label', 'Sumar unidad');
    plus.textContent = '+';

    controls.appendChild(minus);
    controls.appendChild(input);
    controls.appendChild(plus);

    var error = document.createElement('span');
    error.className = 'epl-qty-edit__error';
    error.setAttribute('role', 'status');

    stepper.appendChild(controls);
    stepper.appendChild(error);

    var timer = null;
    function schedule(newQty) {
      /* Respuesta inmediata: numero, precios y spinner en el mismo clic.
         La red solo confirma despues. */
      paintOptimistic(stepper, item, info, newQty);
      setLineLoading(stepper, true);
      clearTimeout(timer);
      timer = setTimeout(function () { commit(stepper, item, info); }, COMMIT_DELAY);
    }

    function clamp(value) {
      if (isNaN(value)) value = item.quantity;
      return Math.max(info.min, Math.min(value, info.max === Infinity ? 1e9 : info.max));
    }

    function step(delta) {
      var value = clamp(parseInt(input.value, 10) + delta);
      input.value = value;
      schedule(value);
    }

    minus.addEventListener('click', function () { step(-1); });
    plus.addEventListener('click', function () { step(1); });
    input.addEventListener('change', function () {
      var value = clamp(parseInt(input.value, 10));
      input.value = value;
      schedule(value);
    });

    return stepper;
  }

  /* Ordena la ficha de la linea quitando lo que no aporta al cliente.
     Cuidado: .product-option se usa TAMBIEN para el precio unitario, que esta
     fuera del <dl>. Por eso todo se acota al <dl>. */
  function tidyDetails(el) {
    var dl = el.querySelector('.cart-item__details dl') || el.querySelector('dl');
    if (!dl) return;

    var rows = dl.querySelectorAll('.product-option');

    Array.prototype.forEach.call(rows, function (row) {
      var dt = row.querySelector('dt');
      var dd = row.querySelector('dd');
      var name = clean(dt && dt.textContent).replace(/:$/, '').toLowerCase();
      var value = clean(dd && dd.textContent).replace(/,$/, '');

      if (isRangeValue(value)) {
        row.style.display = 'none';
        row.dataset.eplHidden = 'tramo';
        return;
      }
      if (name === 'cantidad seleccionada') {
        row.style.display = 'none';
        row.dataset.eplHidden = 'duplicado';
      }
    });

    /* Dawn separa las opciones con {% unless forloop.last %},{% endunless %}.
       Al ocultar la ultima fila, la anterior se queda con una coma colgando. */
    var visible = Array.prototype.filter.call(rows, function (r) {
      return r.style.display !== 'none';
    });
    var last = visible[visible.length - 1];
    if (last) {
      var lastDd = last.querySelector('dd');
      if (lastDd) lastDd.textContent = clean(lastDd.textContent).replace(/,$/, '');
    }
  }

  function enhanceLine(el, item, info) {
    if (!el || el.dataset.eplRangeLocked === '1') return;

    var wrapper = el.querySelector('.cart-item__quantity-wrapper');
    if (!wrapper) return;

    el.dataset.eplRangeLocked = '1';
    el.classList.add(LOCK_CLASS);

    tidyDetails(el);

    var native = wrapper.querySelector('.quantity-popover-container');
    wrapper.insertBefore(buildStepper(item, info), native ? native.nextSibling : wrapper.firstChild);
  }

  /* --- Ruta asincrona: carga inicial y cambios hechos por otros --------- */

  function scan() {
    if (running) { queued = true; return Promise.resolve(); }
    running = true;

    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var pending = [];
        (cart.items || []).forEach(function (item, index) {
          var el = lineElement(document, index + 1);
          if (!el || el.dataset.eplRangeLocked === '1') return;
          var hasRange = (item.options_with_values || []).some(function (o) {
            return isRangeValue(o.value);
          });
          if (!hasRange) return;
          pending.push(
            getProductInfo(item.handle).then(function (info) {
              if (info.tiered) enhanceLine(el, item, info);
            })
          );
        });
        return Promise.all(pending);
      })
      .catch(function () {})
      .finally(function () {
        running = false;
        if (queued) { queued = false; scan(); }
      });
  }

  function scheduleScan() {
    if (suppressScan) return;
    clearTimeout(scanTimer);
    scanTimer = setTimeout(scan, 150);
  }

  function init() {
    scan();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleScan);
    }

    var observer = new MutationObserver(scheduleScan);
    ['cart-drawer', '#main-cart-items', '#CartDrawer-CartItems'].forEach(function (selector) {
      var target = document.querySelector(selector);
      if (target) observer.observe(target, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();